import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { type Nullable } from '@Types';
import { Avatar } from 'Avatar';
import { Button } from 'Button';
import { Text } from 'Text';
import { type MemberDetails } from './ProjectGroup.types';
import { StatusCapsule } from './StatusCapsule';
import { type MembersListItemClassName } from './MembersListItem.types';

export const MembersListItemComponent = ({
    className,
    member,
    showStatus,
    onRemove,
}: {
    className?: MembersListItemClassName;
    member: MemberDetails;
    showStatus: (member: MemberDetails) => boolean;
    onRemove: Nullable<(member: MemberDetails) => void>;
}) => {
    const { t } = useTranslation('back_royal');

    return (
        <div className={twMerge('flex items-start justify-between gap-3', className?.base && className.base(member))}>
            <div className="flex items-start gap-3">
                <Avatar
                    src={member.avatarUrl}
                    name={member.name}
                    className={twMerge('mt-1 shrink-0', className?.avatar && className.avatar(member))}
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Text color="black" weight="semibold">
                            <span data-testid="member-name">{member.name}</span>
                        </Text>
                        {member.detailsStatus && showStatus(member) && <StatusCapsule status={member.detailsStatus} />}
                    </div>
                    <Text color="black">{member.jobTitle}</Text>
                    <Text color="slate-grey">{member.location}</Text>
                </div>
            </div>
            {onRemove && (
                <Button isIconOnly variant="light" onPress={() => onRemove(member)} aria-label={t('remove_member')}>
                    <FontAwesomeIcon icon={faXmark} size="sm" />
                </Button>
            )}
        </div>
    );
};

export const MembersListItem = memo(MembersListItemComponent);
